import { AppBarData } from "@/appBar/appBarDomain";
import { ButtonProps } from "@/components-v2/Button";
import { DropdownMenuItemProps, DropdownMenuProps } from "@/components-v2/Dropdown";
import { poaTypeLabels, PowerOfAttorney, PowerOfAttorneyType } from "@/types/CompanyInfo";
import {
  DCEvent,
  EventDispatcher,
  httpDelete,
  HttpEvent,
  httpGet,
  httpPost,
  HttpResponse,
  navigate,
  showAlert,
  showGeneralError,
  showGeneralSuccess,
  showSuccess,
} from "@/events/eventsDomain";
import { isNorwegianOrgNo } from "@/utils/orgNo";
import { LocalDateFieldModel, NumberFieldModel, RadioFieldModel } from "@/common/commonForms";
import AlertMessageStore from "@/stores/AlertMessageStore";
import { adminPoA2, adminProjects, navigateToPoADetails } from "@/utils/navigation";
import { compact } from "lodash";
import { TabButtonProps } from "@/components-v2/Tabs";
import { RootStoreData } from "@/events/rootStoreData";
import { TagProps } from "@/components-v2/Tag";
import { InfoTagProps } from "@/components-v2/InfoTag";
import { DetailsField } from "@/shareholderRegister/shareholders/shareholderDetailsComponents";
import { when } from "@/utils/functions";
import { LocaleFormat } from "@/i18n/localeFormat";
import { next, prev } from "@/utils/arrays";

export type PowerOfAttorneyState = {
  orgNo?: string;
  powerOfAttorneys?: PowerOfAttorney[];
  isLoading?: boolean;
  isImportingFromBrreg?: boolean;
  dialog?: RegisterPoaDialogState | EditPoaDialogState | RemovePoaDialogState;
  item?: PowerOfAttorney;
};

export type UpdatePowerOfAttorneyRequest = {
  id: string;
  amount?: number;
  dateGiven?: string;
  expiryDate?: string;
  dateRegistered?: string;
};

export type RegisterPoaDialogState = {
  type: "register";
  orgNo: string;
  currency: string;
  isSubmitting?: boolean;
  powerOfAttorneyTypeModel: RadioFieldModel<PowerOfAttorneyType>;
  boardDecidesMaxAmount: RadioFieldModel<boolean>;
  amountModel: NumberFieldModel;
  dateGiven: LocalDateFieldModel;
  expiryDate: LocalDateFieldModel;
  dateRegistered: LocalDateFieldModel;
};

export type RemovePoaDialogState = {
  type: "remove";
  orgNo: string;
  item: PowerOfAttorney;
  isSubmitting?: boolean;
  onSuccess?: DCEvent[];
};

export type RemovePoaDialogProps = {
  item: PowerOfAttorney;
  isSubmitting?: boolean;
  onSubmit: () => void;
  onCancel: () => void;
};

export type EditPoaDialogState = Omit<RegisterPoaDialogState, "type"> & {
  type: "edit";
  item: PowerOfAttorney;
  orgNo: string;
  isSubmitting?: boolean;
};

export type RegisterOrEditPoaDialogProps = {
  state: EditPoaDialogState | RegisterPoaDialogState;
  isBoardDecision: boolean;
  onSubmit: () => void;
  onCancel: () => void;
};

export type PoaDetailsPageProps = {
  canEdit: boolean;
  powerOfAttorneyId?: string;
  navigateBack: () => void;
  companyName: string | null;
  selectedPoaType?: RadioFieldModel<{ type: PowerOfAttorneyType; id: string; dateGiven: string }>;
  infoTags?: InfoTagProps[];
  powerOfAttorneyDetails: DetailsField[];
  selectPrev?: () => void;
  selectNext?: () => void;
  openEditPowerOfAttorneyDialog: () => void;
  openRemovePowerOfAttorneyDialog: () => void;
  messageStore: AlertMessageStore;
};

export type PowerOfAttorneyTab = "show-all" | "active" | "renewal" | "expired";

export type PowerOfAttorneyEmptyStateProps = {
  appBar: AppBarData | null;
  powerOfAttorneyMenu?: DropdownMenuProps;
  importFromBrregButton?: ButtonProps;
  messageStore: AlertMessageStore;
};

export type PowerOfAttorneyActions = {
  openRemovePowerOfAttorneyDialog: (item: PowerOfAttorney) => void;
  openEditPowerOfAttorneyDialog: (item: PowerOfAttorney) => void;
  navigateToPowerOfAttorneyDetails: (id: string) => void;
  onTabChange: (tab: PowerOfAttorneyTab) => void;
  onDismissedRenewalCandidate: (id: string) => void;
  resetDismissedRenewalPoaCandidate: (id: string) => void;
};

export type PowerOfAttorneyProps = {
  canEdit: boolean;
  appBar: AppBarData | null;
  powerOfAttorneyActiveTab: PowerOfAttorneyTab;
  powerOfAttorneyTabs: TabButtonProps[];
  messageStore: AlertMessageStore;
  powerOfAttorneys: PowerOfAttorney[];
  statusTag: (item: PowerOfAttorney) => TagProps;
  powerOfAttorneyMenu?: DropdownMenuProps;
  isLoading?: boolean;
  actions: PowerOfAttorneyActions;
  hasExpiredPoA?: boolean;
  candidatesForRenewalCount: number;
  navigateToProjectLink: string;
};

export type NewPowerOfAttorneyRequest = {
  type: PowerOfAttorneyType;
  dateGiven: string;
  amount?: number;
  expiryDate?: string;
  dateRegistered?: string;
};

export const powerOfAttorneyApi = {
  fetchPowerOfAttorney: ({
    orgNo,
    onSuccess,
    onFailure,
  }: {
    orgNo: string;
    onSuccess: (res: HttpResponse<PowerOfAttorney[]>, state: RootStoreData) => DCEvent[];
    onFailure: (err: unknown, state: RootStoreData) => DCEvent[];
  }): HttpEvent =>
    httpGet({
      url: `/power-of-attorney/${orgNo}`,
      onSuccess,
      onFailure,
    }),

  registerNewPoA: ({
    orgNo,
    data,
    onSuccess,
    onFailure,
  }: {
    orgNo: string;
    data: NewPowerOfAttorneyRequest;
    onSuccess: HttpEvent<string>["onSuccess"];
    onFailure: HttpEvent<void>["onFailure"];
  }): HttpEvent<void> => {
    return httpPost({
      url: `/power-of-attorney/${orgNo}/new`,
      data,
      onSuccess,
      onFailure,
    });
  },

  deletePowerOfAttorney: ({
    orgNo,
    id,
    onSuccess,
    onFailure,
  }: {
    orgNo: string;
    id: string;
    onSuccess: () => DCEvent[];
    onFailure: () => DCEvent[];
  }): HttpEvent => {
    return httpDelete({
      url: `/power-of-attorney/${orgNo}/${id}`,
      onSuccess,
      onFailure,
    });
  },

  updatePowerOfAttorney: ({
    orgNo,
    data,
    onSuccess,
    onFailure,
  }: {
    orgNo: string;
    data: UpdatePowerOfAttorneyRequest;
    onSuccess: () => DCEvent[];
    onFailure: () => DCEvent[];
  }): HttpEvent => {
    return httpPost({
      url: `/power-of-attorney/${orgNo}`,
      data,
      onSuccess,
      onFailure,
    });
  },

  importFromBrreg: ({
    orgNo,
    onSuccess,
    onFailure,
  }: {
    orgNo: string;
    onSuccess: (res: HttpResponse<PowerOfAttorney[]>, state: RootStoreData) => DCEvent[];
    onFailure: () => DCEvent[];
  }): HttpEvent =>
    httpPost({
      url: `/power-of-attorney/${orgNo}/import-from-brreg`,
      onSuccess,
      onFailure,
    }),

  dismissedRenewalCandidate: ({
    id,
    orgNo,
    dismissed,
    onSuccess,
    onFailure,
  }: {
    id: string;
    orgNo: string;
    dismissed: boolean;
    onSuccess: (res: HttpResponse<PowerOfAttorney[]>) => DCEvent[];
    onFailure: () => DCEvent[];
  }) => {
    return httpPost({
      url: `power-of-attorney/${orgNo}/dismiss-renewal/${id}`,
      data: { dismissed },
      onSuccess,
      onFailure,
    });
  },
};

const updatePowerOfAttorneyState = (
  f: (state?: PowerOfAttorneyState) => Partial<PowerOfAttorneyState>,
): DCEvent => ({
  type: "update-store",
  data: {
    powerOfAttorney: (state) => ({
      ...state,
      ...f(state),
    }),
  },
});

export const resetDismissedRenewalPoaCandidate = ({
  id,
  orgNo,
  dismissed,
}: {
  id: string;
  orgNo: string;
  dismissed: boolean;
}): DCEvent[] => {
  return [
    powerOfAttorneyApi.dismissedRenewalCandidate({
      id,
      orgNo,
      dismissed,
      onSuccess: (res) => {
        const isLastRenewalCandidate =
          res.data.filter(
            (p) => p.id !== id && p.isRenewalPoaCandidate && !p.dismissedRenewalCandidate,
          ).length === 0;
        return [
          updatePowerOfAttorneyState(() => ({
            powerOfAttorneys: res.data,
          })),
          { type: "update-company-info" } as const,
          ...(isLastRenewalCandidate ? [navigate(adminPoA2(orgNo, "show-all"))] : []),
          showSuccess({ key: dismissed ? "poa.dismiss.ok" : "poa-flag-as-candidate-renewal.ok" }),
        ];
      },
      onFailure: () => [showGeneralError()],
    }),
  ];
};

export const openRemovePoaDialog = (
  orgNo: string,
  item: PowerOfAttorney | undefined,
  onSuccess?: DCEvent[],
): DCEvent[] => {
  return [
    updatePowerOfAttorneyState(() => ({
      dialog: item ? { type: "remove", item, onSuccess, orgNo } : undefined,
    })),
  ];
};

export const openEditPoaDialog = ({
  dispatch,
  orgNo,
  item,
}: {
  dispatch: EventDispatcher;
  orgNo: string;
  item?: PowerOfAttorney;
}): DCEvent[] => {
  return [
    updatePowerOfAttorneyState(() => ({
      dialog: item ? makeEditPowerOfAttorneyDialogState({ item, dispatch, orgNo }) : undefined,
    })),
  ];
};

const isDialogValid = (dialogState: EditPoaDialogState | RegisterPoaDialogState) => {
  return dialogState.boardDecidesMaxAmount.value
    ? dialogState.boardDecidesMaxAmount.isValid
    : dialogState.amountModel.isValid &&
        dialogState.powerOfAttorneyTypeModel.isValid &&
        dialogState.dateGiven.isValid &&
        dialogState.expiryDate.isValid &&
        dialogState.dateRegistered.isValid;
};
const validateDialog = (dialogState: EditPoaDialogState | RegisterPoaDialogState) => {
  dialogState.powerOfAttorneyTypeModel.validate();
  dialogState.boardDecidesMaxAmount.validate();
  dialogState.amountModel.validate();
  dialogState.dateGiven.validate();
  dialogState.expiryDate.validate();
  dialogState.dateRegistered.validate();
  return isDialogValid(dialogState);
};

const closeDialog = (): DCEvent => updatePowerOfAttorneyState(() => ({ dialog: undefined }));

export const isBoardDecision = (type: PowerOfAttorneyType) =>
  type === PowerOfAttorneyType.DIVIDEND || type === PowerOfAttorneyType.GROUP_CONTRIBUTION;

const onChangeBoardDecides = ({
  item,
  dispatch,
}: {
  item: PowerOfAttorneyType;
  dispatch: EventDispatcher;
}) => {
  if (item === PowerOfAttorneyType.DIVIDEND || item === PowerOfAttorneyType.GROUP_CONTRIBUTION) {
    dispatch([
      updatePowerOfAttorneyState((state) => ({
        dialog: {
          ...state!.dialog!,
          isBoardDecision: true,
        },
      })),
    ]);
  } else {
    dispatch([
      updatePowerOfAttorneyState((state) => ({
        dialog: {
          ...state!.dialog!,
          isBoardDecision: false,
        },
      })),
    ]);
  }
};

const selectPoa = (orgNo: string, id: string): DCEvent[] => {
  return [navigateToPoADetails(orgNo, id)];
};

const fetchPowerOfAttorneys = (orgNo: string): DCEvent[] => [
  updatePowerOfAttorneyState(() => ({
    isLoading: true,
    orgNo,
  })),
  powerOfAttorneyApi.fetchPowerOfAttorney({
    orgNo,
    onSuccess: (res) => [
      updatePowerOfAttorneyState(() => ({
        isLoading: false,
        powerOfAttorneys: res.data,
      })),
    ],
    onFailure: () => [
      updatePowerOfAttorneyState(() => ({
        isLoading: false,
      })),
    ],
  }),
];

export const initPowerOfAttorneyPage = (
  state: PowerOfAttorneyState | undefined,
  orgNo: string,
): DCEvent[] => {
  if (state?.orgNo === orgNo) {
    return [];
  }
  return fetchPowerOfAttorneys(orgNo);
};

export const prepPowerOfAttorneyPage = ({
  appBar,
  orgNo,
  messageStore,
  dispatch,
  canEdit,
  currency,
  powerOfAttorneyTab,
  state,
}: {
  appBar: AppBarData | null;
  orgNo: string;
  messageStore: AlertMessageStore;
  dispatch: EventDispatcher;
  canEdit: boolean;
  currency: string;
  powerOfAttorneyTab?: PowerOfAttorneyTab;
  state: PowerOfAttorneyState;
}): PowerOfAttorneyProps => {
  const { powerOfAttorneys } = state;
  const powerOfAttorneyActiveTab: PowerOfAttorneyTab = powerOfAttorneyTab ?? "show-all";
  return {
    canEdit,
    isLoading: state.isLoading,
    appBar,
    powerOfAttorneyActiveTab,
    powerOfAttorneyTabs: createPowerOfAttorneyTab(
      powerOfAttorneyActiveTab,
      orgNo,
      dispatch,
      powerOfAttorneys,
    ),
    messageStore,
    powerOfAttorneys: powerOfAttorneys ?? [],
    powerOfAttorneyMenu: registerNewPowerOfAttorneyMenu(orgNo, canEdit, currency, dispatch),
    statusTag: (item) => powerOfAttorneyStatusTag(item),
    actions: {
      openRemovePowerOfAttorneyDialog: (item) => dispatch(openRemovePoaDialog(orgNo, item)),
      openEditPowerOfAttorneyDialog: (item) =>
        dispatch(openEditPoaDialog({ dispatch, orgNo, item })),
      navigateToPowerOfAttorneyDetails: (id) => dispatch([navigateToPoADetails(orgNo, id)]),
      onTabChange: (tab) => dispatch([navigateToPowerOfAttorneyTab(orgNo, tab)]),
      onDismissedRenewalCandidate: (id) =>
        dispatch(resetDismissedRenewalPoaCandidate({ id, orgNo, dismissed: true })),
      resetDismissedRenewalPoaCandidate: (id) =>
        dispatch(resetDismissedRenewalPoaCandidate({ id, orgNo, dismissed: false })),
    },
    hasExpiredPoA: powerOfAttorneys?.some((e) => e.hasExpired),
    candidatesForRenewalCount:
      state.powerOfAttorneys?.filter((x) => x.isRenewalPoaCandidate && !x.dismissedRenewalCandidate)
        .length ?? 0,
    navigateToProjectLink: adminProjects(orgNo),
  };
};

export const saveEditedPowerOfAttorney = (editDialogState: EditPoaDialogState): DCEvent[] => {
  if (!validateDialog(editDialogState)) {
    return [];
  }

  const data: UpdatePowerOfAttorneyRequest = {
    id: editDialogState.item.id,
    amount:
      isBoardDecision(editDialogState.powerOfAttorneyTypeModel.value) &&
      editDialogState.boardDecidesMaxAmount.value
        ? undefined
        : editDialogState.amountModel.toNumber(),
    dateGiven: editDialogState.dateGiven.inputValue,
    expiryDate: editDialogState.expiryDate.validatedValue,
    dateRegistered: editDialogState.dateRegistered.validatedValue,
  };

  return [
    updatePowerOfAttorneyState(() => ({
      dialog: {
        ...editDialogState,
        isSubmitting: true,
      },
    })),
    powerOfAttorneyApi.updatePowerOfAttorney({
      orgNo: editDialogState.orgNo,
      data,
      onSuccess: () => [
        closeDialog(),
        showGeneralSuccess(),
        ...fetchPowerOfAttorneys(editDialogState.orgNo),
      ],
      onFailure: () => [closeDialog(), showGeneralError()],
    }),
  ];
};

export const makeEditPowerOfAttorneyDialogState = ({
  item,
  dispatch,
  orgNo,
}: {
  item: PowerOfAttorney;
  dispatch: EventDispatcher;
  orgNo: string;
}): EditPoaDialogState => {
  return {
    type: "edit",
    item,
    orgNo,
    currency: item.currency,
    powerOfAttorneyTypeModel: new RadioFieldModel<PowerOfAttorneyType>({
      value: item.type,
      options: Object.values(PowerOfAttorneyType),
      idFn: (item) => item,
      labelFn: (item) => ({ key: poaTypeLabels[item] }),
      onChange: (item) => onChangeBoardDecides({ item, dispatch }),
    }),
    boardDecidesMaxAmount: new RadioFieldModel({
      value: item.amount !== undefined ? false : true,
      options: [true, false],
      idFn: (v) => v.toString(),
      labelFn: (v) => ({
        key: v ? "poa.boardDecidesMaxAmount.true" : "poa.boardDecidesMaxAmount.false",
      }),
    }),
    amountModel: new NumberFieldModel({ required: true, value: item.amount, isPositive: true }),
    dateGiven: new LocalDateFieldModel({ required: true, value: item.dateGiven }),
    expiryDate: new LocalDateFieldModel({ required: false, value: item.expiryDate }),
    dateRegistered: new LocalDateFieldModel({ value: item.dateRegistered }),
    isSubmitting: false,
  };
};

export const prepPowerOfAttorneyEmptyState = ({
  orgNo,
  canEdit,
  appBar,
  currency,
  messageStore,
  dispatch,
  isImportingFromBrreg,
}: {
  orgNo: string;
  canEdit: boolean;
  appBar: AppBarData | null;
  currency: string;
  messageStore: AlertMessageStore;
  dispatch: EventDispatcher;
  isImportingFromBrreg?: boolean;
}): PowerOfAttorneyEmptyStateProps => ({
  appBar,
  powerOfAttorneyMenu: registerNewPowerOfAttorneyMenu(orgNo, canEdit, currency, dispatch),
  importFromBrregButton:
    canEdit && isNorwegianOrgNo(orgNo)
      ? {
          variant: "secondary",
          label: { key: "poa.emptyState.importFromBrreg" },
          icon: "download",
          loading: isImportingFromBrreg,
          onClick: () => dispatch(importPowerOfAttorneysFromBrreg(orgNo)),
        }
      : undefined,
  messageStore,
});

export const importPowerOfAttorneysFromBrreg = (orgNo: string): DCEvent[] => [
  updatePowerOfAttorneyState(() => ({ isImportingFromBrreg: true })),
  powerOfAttorneyApi.importFromBrreg({
    orgNo,
    onSuccess: (res) => [
      updatePowerOfAttorneyState(() => ({
        isImportingFromBrreg: false,
        powerOfAttorneys: res.data,
      })),
      ...(res.data.length === 0
        ? [showAlert({ key: "poa.import.noneFound" }, "info")]
        : [showGeneralSuccess()]),
    ],
    onFailure: () => [
      updatePowerOfAttorneyState(() => ({ isImportingFromBrreg: false })),
      showGeneralError(),
    ],
  }),
];

export const registerNewPowerOfAttorneyMenu = (
  orgNo: string,
  canEdit: boolean,
  currency: string,
  dispatch: EventDispatcher,
): DropdownMenuProps | undefined => {
  if (!canEdit) return undefined;
  return {
    button: {
      type: "button",
      variant: "secondary",
      label: { key: "poa.register.manually" },
    },
    items: Object.values(PowerOfAttorneyType).map<DropdownMenuItemProps>((item) => ({
      type: "item",
      label: { key: poaTypeLabels[item] },
      onSelect: openRegisterNewPoADialog(item, orgNo, currency, dispatch),
    })),
  };
};

export const openRegisterNewPoADialog = (
  type: PowerOfAttorneyType,
  orgNo: string,
  currency: string,
  dispatch: EventDispatcher,
): DCEvent[] => {
  return [
    updatePowerOfAttorneyState(() => ({
      dialog: makeRegisterNewPowerOfAttorneyDialogState({
        type,
        orgNo,
        dispatch,
        currency,
      }),
    })),
  ];
};

export const makeRegisterNewPowerOfAttorneyDialogState = ({
  type,
  orgNo,
  dispatch,
  currency,
}: {
  type: PowerOfAttorneyType;
  orgNo: string;
  dispatch: EventDispatcher;
  currency: string;
}): RegisterPoaDialogState => ({
  type: "register",
  orgNo,
  currency,
  powerOfAttorneyTypeModel: new RadioFieldModel<PowerOfAttorneyType>({
    value: type,
    options: Object.values(PowerOfAttorneyType),
    idFn: (item) => item,
    labelFn: (item) => ({ key: poaTypeLabels[item] }),
    onChange: (item) => onChangeBoardDecides({ item, dispatch }),
  }),
  boardDecidesMaxAmount: new RadioFieldModel({
    value: true,
    options: [true, false],
    idFn: (v) => v.toString(),
    labelFn: (v) => ({
      key: v ? "poa.boardDecidesMaxAmount.true" : "poa.boardDecidesMaxAmount.false",
    }),
  }),
  amountModel: new NumberFieldModel({ required: true, isPositive: true }),
  dateGiven: new LocalDateFieldModel({ required: true }),
  expiryDate: new LocalDateFieldModel({ required: true }),
  dateRegistered: new LocalDateFieldModel(),
});

export const prepRegisterPoaDialogProps = (
  dispatch: EventDispatcher,
  dialogState: RegisterPoaDialogState,
): RegisterOrEditPoaDialogProps => {
  return {
    state: dialogState,
    isBoardDecision: isBoardDecision(dialogState.powerOfAttorneyTypeModel.value),
    onCancel: () => dispatch([closeDialog()]),
    onSubmit: () => {
      dispatch(
        !validateDialog(dialogState)
          ? []
          : [
              updatePowerOfAttorneyState((state) => ({
                dialog: {
                  ...state!.dialog!,
                  isSubmitting: true,
                },
              })),
              powerOfAttorneyApi.registerNewPoA({
                orgNo: dialogState.orgNo,
                data: {
                  type: dialogState.powerOfAttorneyTypeModel.value,
                  dateGiven: dialogState.dateGiven.validatedValue!,
                  amount:
                    isBoardDecision(dialogState.powerOfAttorneyTypeModel.value) &&
                    dialogState.boardDecidesMaxAmount.value
                      ? undefined
                      : dialogState.amountModel.toNumber(),
                  expiryDate: dialogState.expiryDate.validatedValue,
                  dateRegistered: dialogState.dateRegistered?.validatedValue,
                },
                onSuccess: (response) => [
                  closeDialog(),
                  ...fetchPowerOfAttorneys(dialogState.orgNo),
                  navigateToPoADetails(dialogState.orgNo, response.data),
                  showGeneralSuccess(),
                ],
                onFailure: () => [showGeneralError(), closeDialog()],
              }),
            ],
      );
    },
  };
};

export const removePowerOfAttorney = ({
  orgNo,
  dialogState,
}: {
  orgNo: string;
  dialogState: RemovePoaDialogState;
}): DCEvent[] => {
  return [
    updatePowerOfAttorneyState(() => ({
      dialog: {
        ...dialogState,
        isSubmitting: true,
      },
    })),
    powerOfAttorneyApi.deletePowerOfAttorney({
      id: dialogState.item.id,
      orgNo,
      onSuccess: () => [
        closeDialog(),
        showSuccess({ key: "poa.delete.ok" }),
        updatePowerOfAttorneyState((state) => ({
          powerOfAttorneys: state?.powerOfAttorneys?.filter((f) => f.id !== dialogState.item.id),
        })),
        ...(dialogState?.onSuccess ?? []),
      ],
      onFailure: () => [closeDialog(), showGeneralError()],
    }),
  ];
};

export const prepRemovePoaDialog = (
  dispatch: EventDispatcher,
  dialogState: RemovePoaDialogState,
): RemovePoaDialogProps => {
  return {
    item: dialogState.item,
    isSubmitting: dialogState.isSubmitting,
    onCancel: () => dispatch([closeDialog()]),
    onSubmit: () =>
      dispatch(
        removePowerOfAttorney({
          dialogState,
          orgNo: dialogState.orgNo,
        }),
      ),
  };
};

export const prepEditPoaDialog = (
  dispatch: EventDispatcher,
  dialogState: EditPoaDialogState,
): RegisterOrEditPoaDialogProps => ({
  state: dialogState,
  isBoardDecision: isBoardDecision(dialogState.powerOfAttorneyTypeModel.value),
  onSubmit: () => dispatch(saveEditedPowerOfAttorney(dialogState)),
  onCancel: () => dispatch([closeDialog()]),
});

export const prepPoaDetailsProps = ({
  canEdit,
  orgNo,
  powerOfAttorneyId,
  dispatch,
  companyName,
  items,
  f,
  messageStore,
  powerOfAttorneyTab,
}: {
  canEdit: boolean;
  orgNo: string;
  powerOfAttorneyId: string;
  dispatch: EventDispatcher;
  companyName: string | null;
  items: PowerOfAttorney[];
  f: LocaleFormat;
  messageStore: AlertMessageStore;
  powerOfAttorneyTab?: PowerOfAttorneyTab;
}): PoaDetailsPageProps => {
  const item = items.find((p) => p.id === powerOfAttorneyId);
  const tab = powerOfAttorneyTab === undefined ? "show-all" : powerOfAttorneyTab;
  return {
    canEdit,
    powerOfAttorneyId,
    navigateBack: () => dispatch([navigate(adminPoA2(orgNo, tab))]),
    companyName,
    selectedPoaType: item
      ? new RadioFieldModel<{
          type: PowerOfAttorneyType;
          id: string;
          dateGiven: string;
        }>({
          value: { type: item.type, id: item.id, dateGiven: item.dateGiven },
          options: items.map((item) => ({
            type: item.type,
            id: item.id,
            dateGiven: item.dateGiven,
          })),
          idFn: (option) => option.id,
          labelFn: (option) => ({
            key: poaTypeLabels[option.type],
          }),
          descriptionFn: (option) => f.formatDate(option.dateGiven),
          onChange: (option) => dispatch(selectPoa(orgNo, option.id)),
          iconFn: () => "scroll",
        })
      : undefined,
    infoTags: compact([statusInfoTag(item), expiryInfoTag(item)]),
    powerOfAttorneyDetails: getPoaDetailsFields(companyName, item),
    selectPrev: () =>
      when(
        prev(items, (x) => x.id === powerOfAttorneyId),
        (poa) => dispatch(selectPoa(orgNo, poa.id)),
      ),
    selectNext: () =>
      when(
        next(items, (x) => x.id === powerOfAttorneyId),
        (poa) => dispatch(selectPoa(orgNo, poa.id)),
      ),
    openEditPowerOfAttorneyDialog: () => dispatch(openEditPoaDialog({ dispatch, orgNo, item })),
    openRemovePowerOfAttorneyDialog: () =>
      dispatch(openRemovePoaDialog(orgNo, item, [navigate(adminPoA2(orgNo, tab))])),
    messageStore,
  };
};

export const mapToPowerOfAttorneyTab = (powerOfAttorneyTab?: string): PowerOfAttorneyTab => {
  switch (powerOfAttorneyTab) {
    case "show-all":
    case "active":
    case "expired":
    case "renewal":
      return powerOfAttorneyTab;
    default:
      return "show-all";
  }
};

export function getPowerOfAttorneyCounts(items: PowerOfAttorney[]) {
  const expired = items.filter((x) => x.hasExpired).length;
  return {
    expired,
    active: items.length - expired,
    renewal: items.filter((x) => x.isRenewalPoaCandidate && !x.dismissedRenewalCandidate).length,
  };
}

const navigateToPowerOfAttorneyTab = (
  orgNo: string,
  powerOfAttorneyTab: PowerOfAttorneyTab,
): DCEvent => {
  return navigate(adminPoA2(orgNo, powerOfAttorneyTab));
};

export const createPowerOfAttorneyTab = (
  powerOfAttorneyTab: PowerOfAttorneyTab,
  orgNo: string,
  dispatch: EventDispatcher,
  powerOfAttorneys: PowerOfAttorney[] = [],
): TabButtonProps[] => {
  return compact([
    {
      id: "showAll",
      label: {
        key: "poa.tab.show-all",
        params: {
          count: powerOfAttorneys.length,
        },
      },
      onSelect: () => dispatch([navigateToPowerOfAttorneyTab(orgNo, "show-all")]),
      isActive: powerOfAttorneyTab === "show-all",
    },
    {
      id: "active",
      label: {
        key: "poa.tab.active",
        params: {
          count: getPowerOfAttorneyCounts(powerOfAttorneys).active,
        },
      },
      onSelect: () => dispatch([navigateToPowerOfAttorneyTab(orgNo, "active")]),
      isActive: powerOfAttorneyTab === "active",
      disabled: getPowerOfAttorneyCounts(powerOfAttorneys).active === 0,
    },
    powerOfAttorneys?.filter((x) => x.isRenewalPoaCandidate && !x.dismissedRenewalCandidate)
      .length && {
      id: "renewal",
      label: {
        key: "poa.tab.renewal",
        params: {
          count: getPowerOfAttorneyCounts(powerOfAttorneys).renewal,
        },
      },
      onSelect: () => dispatch([navigateToPowerOfAttorneyTab(orgNo, "renewal")]),
      isActive: powerOfAttorneyTab === "renewal",
    },
    {
      id: "expired",
      label: {
        key: "poa.tab.expired",
        params: {
          count: getPowerOfAttorneyCounts(powerOfAttorneys).expired,
        },
      },
      onSelect: () => dispatch([navigateToPowerOfAttorneyTab(orgNo, "expired")]),
      isActive: powerOfAttorneyTab === "expired",
      disabled: getPowerOfAttorneyCounts(powerOfAttorneys).expired === 0,
    },
  ]);
};

export const powerOfAttorneyStatusTag = (item: PowerOfAttorney): TagProps => ({
  label: { key: item.hasExpired ? "poa.tag.expired" : "poa.tag.active" },
  color: item.hasExpired ? "warning" : "success",
});

export const filterPowerOfAttorneys = (props: PowerOfAttorneyProps): PowerOfAttorney[] => {
  switch (props.powerOfAttorneyActiveTab) {
    case "show-all":
      return props.powerOfAttorneys;
    case "active":
      return props.powerOfAttorneys.filter((x) => !x.hasExpired);
    case "renewal":
      return (
        props.powerOfAttorneys?.filter(
          (x) => x.isRenewalPoaCandidate && !x.dismissedRenewalCandidate,
        ) ?? []
      );
    case "expired":
      return props.powerOfAttorneys.filter((x) => x.hasExpired);
  }
};
const statusInfoTag = (item?: PowerOfAttorney): InfoTagProps | undefined => {
  return item
    ? {
        id: "status",
        icon: {
          name: "circle",
          size: "xs",
          weight: "fill",
          color: item.hasExpired ? "warning" : "success",
        },
        text: { key: item.hasExpired ? "poa.tag.expired" : "poa.tag.active" },
      }
    : undefined;
};

const expiryInfoTag = (item?: PowerOfAttorney): InfoTagProps | undefined => {
  return item && item.expiryDate
    ? {
        id: "expiryDate",
        icon: "calendar-check",
        text: {
          key: item.hasExpired ? "poa.tag.expired.info" : "poa.tag.expires.info",
          params: {
            date: { type: "date", value: item.expiryDate },
          },
        },
      }
    : undefined;
};

const getPoaDetailsFields = (
  companyName: string | null,
  item?: PowerOfAttorney,
): DetailsField[] => {
  return item
    ? [
        ["company", companyName ?? undefined],
        [
          "poa.register-new-dialog-amount",
          item.amount === undefined
            ? { key: "poa.board-decision" }
            : when(item.amount, (value) => ({ type: "number", value })),
        ],
        [
          "poa.dateGiven",
          when(item.dateGiven, (givenDate) => ({ type: "date", value: givenDate })),
        ],
        [
          "poa.expiryDate",
          when(item.expiryDate, (expiryDate) => ({ type: "date", value: expiryDate })),
        ],
        [
          "poa.dateRegistered",
          when(item.dateRegistered, (dateRegistered) => ({ type: "date", value: dateRegistered })),
        ],
      ]
    : [];
};
